import { useEffect, useRef, useState } from 'react'
import type { TgLinkStatusDto } from '@ledokol/contracts'
import { fetchTgLinkStatus } from './api/tg-link'
import { startTgLinkPolling, type StopReason } from './tg-link-polling'

/**
 * Эпик «UI tg-link сотрудника» (T12) P2 — тонкий React wire над pure `startTgLinkPolling`.
 * Сам wire не тестируется отдельно — pure controller покрыт unit-тестами;
 * React-обёртка делает только 3 вещи: useState(status), useEffect(start/stop),
 * visibilitychange handler.
 *
 * @param enabled — true только в state 'code-pending-redeem'. В остальных state'ах хук
 *                  ничего не делает (нет fetch, нет setInterval) — экономия трафика.
 */
export function useTgLinkPolling(enabled: boolean): {
  status: TgLinkStatusDto | null
  stopReason: StopReason | null
} {
  const [status, setStatus] = useState<TgLinkStatusDto | null>(null)
  const [stopReason, setStopReason] = useState<StopReason | null>(null)
  const mountedRef = useRef(true)

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  useEffect(() => {
    if (!enabled) return undefined
    setStopReason(null) // новый старт — сбросить причину

    const handle = startTgLinkPolling({
      fetchStatus: fetchTgLinkStatus,
      onStatus: (s) => setStatus(s),
      onStop: (reason) => setStopReason(reason),
      isMounted: () => mountedRef.current,
    })

    const onVisibility = (): void => {
      if (document.hidden) handle.pause()
      else handle.resume()
    }
    document.addEventListener('visibilitychange', onVisibility)

    return () => {
      document.removeEventListener('visibilitychange', onVisibility)
      handle.stop()
    }
  }, [enabled])

  return { status, stopReason }
}
